import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "completeAddress",
    "streetAddress",
    "addressLine2",
    "landmark",
    "quarter",
    "postalBox",
    "postalCode",
    "city",
    "region",
    "country",
    "countryCode",
    "latitude",
    "longitude",
    "notes"
  ]

  static values = {
    apiKey: String,
    types: { type: Array, default: ["geocode"] }
  }

  connect() {
    console.log("Google Places controller connected")

    this._disconnected = false
    this._processingSelection = false

    this.loadGoogleMapsAPI()
        .then(() => {
          if (this._disconnected) return
          this.initializeAutocomplete()
        })
        .catch((err) => {
          console.error("Google Maps load error:", err)
        })
  }

  disconnect() {
    this._disconnected = true

    if (this.autocomplete && window.google?.maps?.event?.clearInstanceListeners) {
      google.maps.event.clearInstanceListeners(this.autocomplete)
    }
    this.autocomplete = null

    // Nettoyage des listeners DOM
    if (this._onFocus) this.completeAddressTarget?.removeEventListener("focus", this._onFocus)
    if (this._onBlur) this.completeAddressTarget?.removeEventListener("blur", this._onBlur)

    console.log("Google Places controller disconnected")
  }

  // ---------------------------
  // GOOGLE MAPS LOADER (SINGLETON)
  // ---------------------------
  loadGoogleMapsAPI() {
    // 1) Déjà chargé avec Places
    if (window.google?.maps?.places) return Promise.resolve()

    const apiKey =
        this.apiKeyValue ||
        document.querySelector('meta[name="google-places-api-key"]')?.content

    if (!apiKey) return Promise.reject(new Error("Google Places API key not found"))

    // 2) Singleton global
    if (!window.__GO4S_GOOGLE_MAPS_PROMISE__) {
      window.__GO4S_GOOGLE_MAPS_PROMISE__ = new Promise((resolve, reject) => {
        if (window.google?.maps?.places) return resolve()

        const callbackName = "__go4sInitGooglePlaces"

        window[callbackName] = () => {
          try {
            if (!window.google?.maps?.places) {
              reject(
                  new Error(
                      "Google Maps loaded but Places library is missing (libraries=places)."
                  )
              )
              return
            }
            resolve()
          } finally {
            try {
              delete window[callbackName]
            } catch (_) {
              window[callbackName] = undefined
            }
          }
        }

        // Si un script existe déjà, on n'en ajoute pas un autre
        const existingScript =
            document.querySelector('script[data-google-maps="true"]') ||
            [...document.querySelectorAll("script")].find((s) =>
                (s.src || "").includes("maps.googleapis.com/maps/api/js")
            )

        if (existingScript) {
          existingScript.addEventListener("load", () => {
            if (window.google?.maps?.places) resolve()
            else reject(new Error("Existing Google Maps script loaded but Places is missing."))
          })
          existingScript.addEventListener("error", () => {
            reject(new Error("Existing Google Maps script failed to load"))
          })
          return
        }

        const script = document.createElement("script")
        script.dataset.googleMaps = "true"
        script.async = true
        script.defer = true
        script.src =
            `https://maps.googleapis.com/maps/api/js` +
            `?key=${encodeURIComponent(apiKey)}` +
            `&libraries=places` +
            `&callback=${callbackName}` +
            `&v=weekly`

        script.onerror = () => {
          try {
            delete window[callbackName]
          } catch (_) {
            window[callbackName] = undefined
          }
          reject(new Error("Failed to load Google Maps JavaScript API script"))
        }

        document.head.appendChild(script)
      })
    }

    return window.__GO4S_GOOGLE_MAPS_PROMISE__
  }

  // ---------------------------
  // AUTOCOMPLETE INIT
  // ---------------------------
  initializeAutocomplete() {
    console.log("Initializing Google Places autocomplete")

    if (!this.hasCompleteAddressTarget) {
      console.warn("No completeAddress target found")
      return
    }

    if (!window.google?.maps?.places?.Autocomplete) {
      console.error("Places Autocomplete is not available. Check libraries=places.")
      return
    }

    // Évite double init
    if (this.autocomplete) {
      try {
        google.maps.event.clearInstanceListeners(this.autocomplete)
      } catch (_) {}
      this.autocomplete = null
    }

    const options = {
      types: this.typesValue,
      fields: ["formatted_address", "address_components", "geometry", "place_id", "name"]
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.completeAddressTarget, options)

    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete.getPlace()
      this.handlePlaceSelection(place)
    })

    // IMPORTANT: si on a masqué le dropdown (pac-container) après sélection,
    // on le réactive au prochain focus.
    this._onFocus = () => this.showPacContainers()
    this._onBlur = () => {
      // petite sécurité : si blur sans sélection, on laisse Google gérer
      // mais on peut nettoyer un container "resté" si besoin
      if (!this._processingSelection) return
    }

    this.completeAddressTarget.addEventListener("focus", this._onFocus)
    this.completeAddressTarget.addEventListener("blur", this._onBlur)

    console.log("✅ Autocomplete initialized successfully")
  }

  // ---------------------------
  // PLACE HANDLING
  // ---------------------------
  handlePlaceSelection(place) {
    if (!place) return

    // Quand l'utilisateur clique une suggestion,
    // on passe en mode "processing" pour éviter que les events rouvrent la liste.
    this._processingSelection = true

    try {
      // Parfois place.address_components est vide mais formatted_address existe
      if (!place.address_components || place.address_components.length === 0) {
        if (place.formatted_address && this.hasCompleteAddressTarget) {
          this.completeAddressTarget.value = place.formatted_address
        }
        // Ferme quand même
        this.closeAutocompleteDropdown()
        return
      }

      this.processPlace(place)
    } finally {
      // On relâche un peu plus tard pour laisser les events se propager
      setTimeout(() => {
        this._processingSelection = false
      }, 0)
    }
  }

  processPlace(place) {
    this.clearAllFields()

    // Adresse complète
    if (place.formatted_address && this.hasCompleteAddressTarget) {
      this.completeAddressTarget.value = place.formatted_address
    }

    // Coordonnées
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      if (this.hasLatitudeTarget) this.latitudeTarget.value = lat.toFixed(6)
      if (this.hasLongitudeTarget) this.longitudeTarget.value = lng.toFixed(6)
    }

    // Composants
    const addressData = this.parseAddressComponents(place.address_components)
    this.fillAllFields(addressData)

    // ⚠️ Déclencher les events peut rouvrir le dropdown.
    // On les garde, mais on ferme le dropdown juste après.
    this.triggerAllEvents()

    // ✅ ferme le dropdown proprement
    this.closeAutocompleteDropdown()
  }

  // ---------------------------
  // DROPDOWN CLOSE / OPEN HELPERS
  // ---------------------------
  closeAutocompleteDropdown() {
    // 1) Masquer immédiatement les containers pac-container
    this.hidePacContainers()

    // 2) Retirer le focus de l'input pour que Google ferme son panel
    try {
      this.completeAddressTarget?.blur()
    } catch (_) {}

    // 3) Masquer à nouveau après un court délai (au cas où)
    setTimeout(() => this.hidePacContainers(), 50)
    setTimeout(() => this.hidePacContainers(), 150)
  }

  hidePacContainers() {
    document.querySelectorAll(".pac-container").forEach((el) => {
      el.style.display = "none"
      el.style.visibility = "hidden"
    })
  }

  showPacContainers() {
    // Si on a caché un pac-container, on le réactive au focus suivant
    document.querySelectorAll(".pac-container").forEach((el) => {
      el.style.display = ""
      el.style.visibility = ""
    })
  }

  // ---------------------------
  // PARSING / FILLING
  // ---------------------------
  parseAddressComponents(components) {
    const data = {
      streetNumber: "",
      route: "",
      subpremise: "",
      premise: "",
      establishment: "",
      pointOfInterest: "",
      neighborhood: "",
      sublocality: "",
      sublocalityLevel1: "",
      sublocalityLevel2: "",
      locality: "",
      administrativeAreaLevel1: "",
      administrativeAreaLevel2: "",
      administrativeAreaLevel3: "",
      country: "",
      countryCode: "",
      postalCode: ""
    }

    components.forEach((component) => {
      const { types, long_name: longName, short_name: shortName } = component

      types.forEach((type) => {
        switch (type) {
          case "street_number":
            data.streetNumber = longName
            break
          case "route":
            data.route = longName
            break
          case "subpremise":
            data.subpremise = longName
            break
          case "premise":
            data.premise = longName
            break
          case "establishment":
            data.establishment = longName
            break
          case "point_of_interest":
            data.pointOfInterest = longName
            break
          case "neighborhood":
            data.neighborhood = longName
            break
          case "sublocality":
            data.sublocality = longName
            break
          case "sublocality_level_1":
            data.sublocalityLevel1 = longName
            break
          case "sublocality_level_2":
            data.sublocalityLevel2 = longName
            break
          case "locality":
            data.locality = longName
            break
          case "administrative_area_level_1":
            data.administrativeAreaLevel1 = longName
            break
          case "administrative_area_level_2":
            data.administrativeAreaLevel2 = longName
            break
          case "administrative_area_level_3":
            data.administrativeAreaLevel3 = longName
            break
          case "country":
            data.country = longName
            data.countryCode = shortName
            break
          case "postal_code":
            data.postalCode = longName
            break
        }
      })
    })

    return data
  }

  fillAllFields(data) {
    if (this.hasStreetAddressTarget) {
      this.streetAddressTarget.value = [data.streetNumber, data.route].filter(Boolean).join(" ")
    }

    if (this.hasAddressLine2Target) {
      this.addressLine2Target.value = [data.subpremise, data.premise].filter(Boolean).join(", ")
    }

    if (this.hasLandmarkTarget) {
      this.landmarkTarget.value = data.establishment || data.pointOfInterest || ""
    }

    if (this.hasQuarterTarget) {
      // Only use actual neighborhood/sublocality data for quarter field
      // Do NOT use administrative areas (departments, arrondissements) as they are not neighborhoods
      this.quarterTarget.value =
          data.neighborhood ||
          data.sublocalityLevel1 ||
          data.sublocalityLevel2 ||
          data.sublocality ||
          ""
    }

    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = data.postalCode
    if (this.hasCityTarget) this.cityTarget.value = data.locality
    if (this.hasRegionTarget) this.regionTarget.value = data.administrativeAreaLevel1

    // Pour le pays, on doit gérer SlimSelect
    if (this.hasCountryTarget) {
      this.setSelectValue(this.countryTarget, data.countryCode)
    }
    if (this.hasCountryCodeTarget) this.countryCodeTarget.value = data.countryCode
  }

  // Met à jour un select, y compris s'il utilise SlimSelect
  setSelectValue(selectElement, value) {
    if (!selectElement || !value) return

    // Mettre la valeur sur le select natif
    selectElement.value = value

    // Si SlimSelect est utilisé, on doit le synchroniser
    if (selectElement.slim) {
      // SlimSelect stocke son instance sur l'élément
      selectElement.slim.setSelected(value)
    } else if (window.SlimSelect) {
      // Fallback: déclencher un événement pour forcer la mise à jour
      // et recréer SlimSelect si nécessaire
      const slimContainer = selectElement.nextElementSibling
      if (slimContainer?.classList?.contains("ss-main")) {
        // SlimSelect est présent mais pas accessible via .slim
        // On force une réinitialisation
        selectElement.dataset.slimInitialized = "false"
        if (typeof window.initSlimSelects === "function") {
          setTimeout(() => window.initSlimSelects(), 10)
        }
      }
    }

    // Déclencher les events
    selectElement.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clearAllFields() {
    const fields = [
      "streetAddress",
      "addressLine2",
      "landmark",
      "quarter",
      "postalBox",
      "postalCode",
      "city",
      "region",
      "country",
      "countryCode",
      "latitude",
      "longitude"
    ]

    fields.forEach((field) => {
      const targetName = `${field}Target`
      const hasMethod = `has${field.charAt(0).toUpperCase() + field.slice(1)}Target`
      if (this[hasMethod] && this[targetName]) this[targetName].value = ""
    })
  }

  triggerAllEvents() {
    const fields = [
      "completeAddress",
      "streetAddress",
      "addressLine2",
      "landmark",
      "quarter",
      "postalBox",
      "postalCode",
      "city",
      "region",
      "country",
      "countryCode",
      "latitude",
      "longitude"
    ]

    fields.forEach((field) => {
      const targetName = `${field}Target`
      const hasMethod = `has${field.charAt(0).toUpperCase() + field.slice(1)}Target`

      if (this[hasMethod] && this[targetName]) {
        const element = this[targetName]

        // ⚠️ Pendant la sélection, ces events peuvent rouvrir le dropdown.
        // On les garde pour Rails, mais le closeAutocompleteDropdown() juste après corrige l'UI.
        element.dispatchEvent(new Event("input", { bubbles: true }))
        element.dispatchEvent(new Event("change", { bubbles: true }))

        if (element.tagName === "SELECT") {
          element.dispatchEvent(new Event("blur", { bubbles: true }))
          element.dispatchEvent(
              new CustomEvent("stimulus:update", {
                bubbles: true,
                detail: { value: element.value }
              })
          )
        }
      }
    })
  }
}
